import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Filter pill components (DESIGN-SPEC.md §4.4 "Filter pills & segmented
 * controls")
 *
 * New for the Institutional Console redesign (issue #233). Used for
 * type/category quick-filter rows above tables (e.g. Holdings "All types /
 * Stocks / ETFs / Crypto / Metals", Events severity filters, Transactions
 * "Gains / Losses"). Geometry: h26, r5, 11.5px text. Active state uses
 * `--accent-tint` bg + `--accent-text` at 500 weight; inactive uses
 * `--text-muted` with a faint hover background. See Holdings.dc.html
 * (lines ~68-74) for the pixel-truth markup this mirrors.
 *
 * Implemented as a real `<button>` (the mockup uses `<span>` for brevity)
 * so filter pills are keyboard-operable and screen-reader-focusable without
 * extra plumbing — no functionality is lost relative to the mockup, only
 * gained (§7 a11y checklist: all icon/interactive controls need real
 * semantics).
 *
 * @example
 * <FilterPillGroup>
 *   <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>All types</FilterPill>
 *   <FilterPill active={filter === 'stock'} onClick={() => setFilter('stock')}>Stocks</FilterPill>
 * </FilterPillGroup>
 */
const FilterPill = React.forwardRef(
  ({ className, active = false, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      className={cn(
        "inline-flex h-[26px] items-center justify-center whitespace-nowrap rounded-[5px] px-2.5 text-[11.5px] transition-colors duration-[120ms] ease-out",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
        active
          ? "bg-accent-tint font-medium text-[var(--accent-text,var(--color-link))]"
          : // Issue #238 fix (issue-235 design review advisory A2): inactive
            // hover was a hardcoded white overlay, invisible/incorrect on
            // console-light. Routed through --button-hover-bg (same token
            // family the outline Button already uses per its #233 fix) so
            // each theme supplies its own visible hover fill.
            "text-[var(--text-muted,var(--color-text-tertiary))] hover:bg-[var(--button-hover-bg,rgba(255,255,255,0.04))]",
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
FilterPill.displayName = "FilterPill";

/**
 * FilterPillGroup - layout wrapper for a row of FilterPill buttons.
 * Matches the mockup's `gap: 6px` row (Holdings.dc.html line 68).
 */
const FilterPillGroup = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      role="group"
      className={cn("flex flex-wrap items-center gap-1.5", className)}
      {...props}
    >
      {children}
    </div>
  )
);
FilterPillGroup.displayName = "FilterPillGroup";

export { FilterPill, FilterPillGroup };
